import { Ban, Check, Copy, FileUp, KeyRound, MailPlus, Pencil, Plus, Search, UserRoundCheck, Users } from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { Modal } from '../components/Modal'
import { PageTitle } from '../components/PageTitle'
import { StatusBadge, SyncBadge } from '../components/StatusBadge'
import { useUIStore } from '../store/ui'
import type { AppMeta, Employee } from '../types'

interface EmployeeForm {
  name: string
  email: string
  quotaMB: number
  password: string
}

const emptyForm: EmployeeForm = { name: '', email: '', quotaMB: 2048, password: '' }

export function EmployeesPage() {
  const [items, setItems] = useState<Employee[]>([])
  const [meta, setMeta] = useState<AppMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Employee | null>(null)
  const [form, setForm] = useState<EmployeeForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [credential, setCredential] = useState<{ email: string; password: string } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [csvText, setCSVText] = useState('姓名,邮箱,容量MB\n')
  const [importResults, setImportResults] = useState<ImportResult[] | null>(null)
  const notify = useUIStore((state) => state.notify)

  async function load() {
    setLoading(true)
    try {
      const [employees, appMeta] = await Promise.all([api<{ items: Employee[] }>('/employees'), api<AppMeta>('/meta')])
      setItems(employees.items || [])
      setMeta(appMeta)
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '加载员工失败', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((item) => item.name.toLowerCase().includes(term) || item.email.toLowerCase().includes(term))
  }, [items, search])

  function openCreate() {
    setEditing(null)
    setForm({ ...emptyForm, email: `@${meta?.domain || 'bluewhalewanders.net'}` })
    setFormOpen(true)
  }

  function openEdit(employee: Employee) {
    setEditing(employee)
    setForm({ name: employee.name, email: employee.email, quotaMB: employee.quotaMB, password: '' })
    setFormOpen(true)
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    try {
      if (editing) {
        await api<Employee>(`/employees/${editing.id}`, { method: 'PATCH', body: JSON.stringify({ name: form.name, quotaMB: Number(form.quotaMB) }) })
        notify('员工邮箱资料已更新')
      } else {
        const result = await api<{ employee: Employee; temporaryPassword: string }>('/employees', { method: 'POST', body: JSON.stringify({ ...form, quotaMB: Number(form.quotaMB) }) })
        setCredential({ email: result.employee.email, password: result.temporaryPassword })
        notify('员工邮箱已创建')
      }
      setFormOpen(false)
      await load()
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '保存失败', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function toggleStatus(employee: Employee) {
    const disabled = employee.status === 'active'
    if (disabled && !window.confirm(`确定停用 ${employee.email}？停用后该员工将无法登录邮箱。`)) return
    try {
      await api(`/employees/${employee.id}/status`, { method: 'POST', body: JSON.stringify({ disabled }) })
      notify(disabled ? '员工邮箱已停用' : '员工邮箱已恢复')
      await load()
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '状态更新失败', 'error')
    }
  }

  async function resetPassword(employee: Employee) {
    if (!window.confirm(`为 ${employee.email} 生成新的临时密码？旧密码将立即失效。`)) return
    try {
      const result = await api<{ temporaryPassword: string }>(`/employees/${employee.id}/password`, { method: 'POST', body: JSON.stringify({ password: '' }) })
      setCredential({ email: employee.email, password: result.temporaryPassword })
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '密码重置失败', 'error')
    }
  }

  async function importCSV() {
    setSaving(true)
    try {
      const result = await api<{ items: ImportResult[] }>('/employees/import', { method: 'POST', body: JSON.stringify({ csv: csvText }) })
      setImportResults(result.items)
      await load()
      const succeeded = result.items.filter((item) => !item.error).length
      notify(`批量处理完成，成功 ${succeeded} 条`, result.items.some((item) => item.error) ? 'info' : 'success')
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '批量导入失败', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-stack">
      <PageTitle icon={<Users size={22} />} title="员工邮箱" description="创建员工账号、分配邮箱容量，并管理登录状态。" action={<div className="button-row"><button className="button secondary" onClick={() => { setImportResults(null); setImportOpen(true) }}><FileUp size={17} />批量导入</button><button className="button primary" onClick={openCreate}><Plus size={17} />开通邮箱</button></div>} />
      <section className="panel table-panel">
        <div className="table-toolbar">
          <div className="search-box"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索员工姓名或邮箱" /></div>
          <span className="record-count">共 {filtered.length} 名员工</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>员工</th><th>状态</th><th>邮箱容量</th><th>Mox 同步</th><th>创建时间</th><th className="align-right">操作</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6}><div className="empty-row">正在加载员工…</div></td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={6}><div className="empty-row"><UserRoundCheck size={28} />没有找到员工邮箱</div></td></tr>}
              {filtered.map((employee) => <tr key={employee.id}>
                <td><div className="person-cell"><span className="person-avatar">{employee.name.slice(0, 1)}</span><div><strong>{employee.name}</strong><small>{employee.email}</small></div></div></td>
                <td><StatusBadge status={employee.status} /></td>
                <td>{formatQuota(employee.quotaMB)}</td>
                <td><SyncBadge status={employee.syncStatus} error={employee.syncError} /></td>
                <td className="muted-cell nowrap">{new Date(employee.createdAt).toLocaleDateString('zh-CN')}</td>
                <td><div className="row-actions"><button className="icon-button" title="编辑" onClick={() => openEdit(employee)}><Pencil size={17} /></button><button className="icon-button" title="重置密码" onClick={() => void resetPassword(employee)}><KeyRound size={17} /></button><button className={`icon-button ${employee.status === 'disabled' ? 'positive' : 'danger'}`} title={employee.status === 'disabled' ? '恢复' : '停用'} onClick={() => void toggleStatus(employee)}>{employee.status === 'disabled' ? <Check size={17} /> : <Ban size={17} />}</button></div></td>
              </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <Modal open={formOpen} title={editing ? '编辑员工邮箱' : '开通员工邮箱'} description={editing ? editing.email : '创建 Mox 账户并生成员工初始密码。'} onClose={() => setFormOpen(false)} footer={<><button className="button ghost" onClick={() => setFormOpen(false)}>取消</button><button className="button primary" form="employee-form" disabled={saving}>{saving ? '正在保存…' : editing ? '保存修改' : '确认开通'}</button></>}>
        <form id="employee-form" className="form-grid" onSubmit={save}>
          <label className="field full"><span>员工姓名</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="例如：张三" required autoFocus /></label>
          <label className="field full"><span>邮箱地址</span><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} disabled={Boolean(editing)} required /><small>邮箱创建后地址不可修改</small></label>
          <label className="field"><span>邮箱容量</span><select value={form.quotaMB} onChange={(e) => setForm({ ...form, quotaMB: Number(e.target.value) })}><option value={1024}>1 GB</option><option value={2048}>2 GB</option><option value={4096}>4 GB</option><option value={10240}>10 GB</option><option value={20480}>20 GB</option></select></label>
          {!editing && <label className="field"><span>初始密码（可选）</span><input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="留空自动生成" minLength={8} maxLength={256} autoComplete="new-password" /><small>至少 8 位；留空由系统生成</small></label>}
        </form>
      </Modal>

      <CredentialModal credential={credential} onClose={() => setCredential(null)} />

      <Modal open={importOpen} title="批量导入员工" description="使用 CSV 一次创建多个员工邮箱。" size="large" onClose={() => setImportOpen(false)} footer={<><button className="button ghost" onClick={() => setImportOpen(false)}>关闭</button>{!importResults && <button className="button primary" disabled={saving} onClick={() => void importCSV()}>{saving ? '正在导入…' : '开始导入'}</button>}</>}>
        {!importResults ? <>
          <div className="upload-guide"><FileUp size={22} /><div><strong>CSV 列：姓名、邮箱、容量MB</strong><span>第一行为表头，容量留空时默认 2048 MB。</span></div><label className="button secondary compact">选择文件<input type="file" accept=".csv,text/csv" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void file.text().then(setCSVText) }} /></label></div>
          <label className="field"><span>CSV 内容</span><textarea className="csv-area" value={csvText} onChange={(event) => setCSVText(event.target.value)} /></label>
        </> : <ImportResults items={importResults} />}
      </Modal>
    </div>
  )
}

interface ImportResult { row: number; email: string; temporaryPassword?: string; error?: string }

function ImportResults({ items }: { items: ImportResult[] }) {
  return <div className="import-results"><div className="result-summary"><strong>导入结果</strong><span>成功 {items.filter((item) => !item.error).length} 条，失败 {items.filter((item) => item.error).length} 条</span></div><div className="table-wrap compact-table"><table><thead><tr><th>行</th><th>邮箱</th><th>临时密码 / 错误</th></tr></thead><tbody>{items.map((item) => <tr key={item.row}><td>{item.row}</td><td>{item.email || '—'}</td><td className={item.error ? 'text-danger' : 'password-cell'}>{item.error || item.temporaryPassword}</td></tr>)}</tbody></table></div><p className="callout warning">临时密码只显示这一次，请立即安全保存并发送给员工。</p></div>
}

function CredentialModal({ credential, onClose }: { credential: { email: string; password: string } | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return <Modal open={Boolean(credential)} title="邮箱已准备好" description="请将临时密码安全地交给员工。" size="small" onClose={onClose} footer={<button className="button primary" onClick={onClose}>我已保存</button>}>
    {credential && <div className="credential-card"><div><span>邮箱地址</span><strong>{credential.email}</strong></div><div><span>临时密码</span><code>{credential.password}</code></div><button className="button secondary wide" onClick={() => { void navigator.clipboard.writeText(`邮箱：${credential.email}\n临时密码：${credential.password}`); setCopied(true) }}>{copied ? <><Check size={17} />已复制</> : <><Copy size={17} />复制账号信息</>}</button><p><KeyRound size={16} />员工登录后应立即修改密码</p></div>}
  </Modal>
}

function formatQuota(mb: number) { return mb >= 1024 ? `${Number((mb / 1024).toFixed(1))} GB` : `${mb} MB` }
